import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { OwnerInitiativeDialog } from "./center/OwnerQuickDialogs";
import { surfaceSx } from "./center/ownerCenterUi";
import { ownerProjectNextActionLabel, ownerProjectNextActionText } from "./projects/ownerProjectNextAction";

const STATUS_LABELS = { active: "Activo", paused: "Pausado", done: "Terminado", archived: "Archivado" };
const PRIORITY_LABELS = { high: "Alta", normal: "Normal", low: "Baja" };
const WORKFLOW_LABELS = { clear: "En orden", ready: "Listo", steady: "En orden", focus: "En foco", waiting: "Esperando", blocked: "Bloqueado", collection: "Cobrar", maintenance: "Mantenimiento" };

function idOf(value) { return String(value?.id || value?._id || value || ""); }
function hoursLabel(minutes) {
  const value = Math.max(0, Number(minutes || 0));
  if (!value) return "0 h";
  const hours = Math.floor(value / 60);
  const rest = Math.round(value % 60);
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}
function safeUrl(value) {
  try {
    const url = new URL(String(value || ""));
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : "";
  } catch { return ""; }
}
function money(value, currency = "ARS") {
  try { return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0)); }
  catch { return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`; }
}
function shortDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(date);
}
function financeLine(finance = {}) {
  const currencies = new Set([...Object.keys(finance.incomeByCurrency || {}), ...Object.keys(finance.expenseByCurrency || {})]);
  return [...currencies].map((currency) => ({
    currency,
    income: Number(finance.incomeByCurrency?.[currency] || 0),
    expense: Number(finance.expenseByCurrency?.[currency] || 0),
    margin: Number(finance.marginByCurrency?.[currency] || 0),
  }));
}

export default function OwnerProjectsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [status, setStatus] = React.useState("active");
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const response = await ownerApi.listInitiatives({ status: status === "all" ? "" : status, limit: 100 });
      setRows(response?.initiatives || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tus proyectos."));
    } finally {
      setLoading(false);
    }
  }, [status]);

  React.useEffect(() => { load(); }, [load]);

  const visible = React.useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es");
    if (!needle) return rows;
    return rows.filter((row) => [row.name, row.outcome, ownerProjectNextActionText(row), row.nextStep, row.client?.name, row.client?.company, row.notes]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase("es").includes(needle)));
  }, [rows, query]);

  const openProject = (row) => navigate(`/dashboard/owner/proyectos/${encodeURIComponent(idOf(row))}`);

  const deleteProject = async (row) => {
    const projectId = idOf(row);
    if (!projectId || deletingId) return;
    const confirmed = window.confirm(`¿Eliminar ${row.name}?\n\nLa ficha, contrato y bloques se eliminan. Los movimientos, pagos, tareas y horas quedan conservados y simplemente se desvinculan.`);
    if (!confirmed) return;
    setDeletingId(projectId);
    setError("");
    try {
      await ownerApi.deleteInitiative(projectId);
      setRows((current) => current.filter((item) => idOf(item) !== projectId));
    } catch (err) {
      setError(formatApiError(err, "No pude eliminar el proyecto."));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Box sx={{ height: "100%", minHeight: 0, overflow: "auto", p: { xs: .75, md: 1 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} sx={{ mb: .8 }}>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar proyecto, cliente o próximo paso"
          sx={{ flex: 1, minWidth: 0 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField select size="small" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ width: { xs: "100%", sm: 150 } }}>
          <MenuItem value="active">Activos</MenuItem>
          <MenuItem value="paused">Pausados</MenuItem>
          <MenuItem value="done">Terminados</MenuItem>
          <MenuItem value="archived">Archivados</MenuItem>
          <MenuItem value="all">Todos</MenuItem>
        </TextField>
        <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}>
          Nuevo proyecto
        </Button>
      </Stack>

      {error ? <Alert severity="error" action={<Button size="small" onClick={load}>Reintentar</Button>} sx={{ mb: .8 }}>{error}</Alert> : null}

      {!loading && !visible.length ? (
        <Box sx={{ ...surfaceSx, p: 2.2, textAlign: "center" }}>
          <FolderOpenRoundedIcon sx={{ fontSize: 34, color: "text.disabled" }} />
          <Typography sx={{ mt: .5, fontWeight: 950 }}>{query ? "No encontré proyectos con ese filtro." : "Todavía no hay proyectos acá."}</Typography>
          <Button size="small" onClick={() => setCreateOpen(true)} sx={{ mt: .7, textTransform: "none", fontWeight: 900 }}>Crear proyecto</Button>
        </Box>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))", xl: "repeat(3,minmax(0,1fr))" }, gap: .75 }}>
        {visible.map((row) => {
          const activities = row.activities || {};
          const driveUrl = safeUrl(row.driveFolderUrl);
          const finance = row.finance || {};
          const recurringLines = financeLine(finance);
          const nextIncome = finance.nextIncome || null;
          const workflowState = row.workPlan?.state || row.workflow?.state || "steady";
          return (
            <Box
              key={idOf(row)}
              role="button"
              tabIndex={0}
              onClick={() => openProject(row)}
              onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openProject(row); } }}
              sx={{
                ...surfaceSx,
                p: 1.15,
                minWidth: 0,
                cursor: "pointer",
                transition: "transform .12s ease, border-color .12s ease",
                "&:hover": { transform: "translateY(-1px)", borderColor: "rgba(243,90,58,.28)" },
              }}
            >
              <Stack direction="row" spacing={.7} alignItems="flex-start">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 1000, fontSize: 15.5 }}>{row.name}</Typography>
                  <Typography noWrap sx={{ mt: .1, color: "text.secondary", fontWeight: 750, fontSize: 12 }}>
                    {row.client?.name || row.client?.company || row.outcome || "Sin cliente vinculado"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={.15}>
                  <Tooltip title="Eliminar proyecto"><span><IconButton color="error" size="small" disabled={deletingId === idOf(row)} onClick={(event) => { event.stopPropagation(); deleteProject(row); }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Abrir ficha"><IconButton size="small" onClick={(event) => { event.stopPropagation(); openProject(row); }}><ArrowForwardRoundedIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap" sx={{ mt: .8 }}>
                <Chip size="small" label={STATUS_LABELS[row.status] || row.status || "Activo"} sx={{ height: 22, fontWeight: 850, fontSize: 11 }} />
                <Chip size="small" color={workflowState === "blocked" || workflowState === "collection" ? "warning" : workflowState === "focus" ? "primary" : "default"} variant={workflowState === "steady" ? "outlined" : "filled"} label={WORKFLOW_LABELS[workflowState] || workflowState} sx={{ height: 22, fontWeight: 850, fontSize: 11 }} />
                <Chip size="small" variant="outlined" label={`Prioridad ${PRIORITY_LABELS[row.priority] || row.priority || "Normal"}`} sx={{ height: 22, fontWeight: 800, fontSize: 11 }} />
                {activities.overdueCount ? <Chip size="small" color="warning" label={`${activities.overdueCount} vencida${activities.overdueCount === 1 ? "" : "s"}`} sx={{ height: 22, fontWeight: 850, fontSize: 11 }} /> : null}
              </Stack>

              <Box sx={{ mt: .9, p: .75, borderRadius: 1.5, bgcolor: "action.hover", minHeight: 52 }}>
                <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".03em" }}>{ownerProjectNextActionLabel(row)}</Typography>
                <Typography sx={{ mt: .15, fontWeight: 800, fontSize: 12.5, lineHeight: 1.3, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>
                  {ownerProjectNextActionText(row, "Sin próximo paso definido")}
                </Typography>
              </Box>

              {recurringLines.length || nextIncome ? <Box sx={{ mt: .65, px: .75, py: .6, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={.45} alignItems="center"><PaymentsRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} /><Typography sx={{ fontWeight: 900, fontSize: 10.5, color: "text.secondary" }}>DINERO RECURRENTE</Typography></Stack>
                {recurringLines.map((line) => <Typography key={line.currency} noWrap sx={{ mt: .15, fontWeight: 900, fontSize: 11.5 }}>+{money(line.income, line.currency)}/mes · −{money(line.expense, line.currency)} · margen {money(line.margin, line.currency)}</Typography>)}
                {nextIncome ? <Typography noWrap sx={{ mt: .15, color: nextIncome.dueState === "overdue" ? "warning.main" : "text.secondary", fontWeight: 800, fontSize: 10.8 }}>Próximo cobro {shortDate(nextIncome.dueAt)} · {money(nextIncome.amount, nextIncome.currency)}{nextIncome.dueState === "overdue" ? " · vencido" : ""}</Typography> : null}
              </Box> : null}

              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: .8, color: "text.secondary" }}>
                <Stack direction="row" spacing={.35} alignItems="center"><TaskAltRoundedIcon sx={{ fontSize: 15 }} /><Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>{activities.openCount || 0} abiertas</Typography></Stack>
                <Stack direction="row" spacing={.35} alignItems="center"><AccessTimeRoundedIcon sx={{ fontSize: 15 }} /><Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>{hoursLabel(row.actualMinutes)}</Typography></Stack>
                {driveUrl ? <Tooltip title="Abrir Drive"><IconButton component="a" href={driveUrl} target="_blank" rel="noreferrer" size="small" onClick={(event) => event.stopPropagation()} sx={{ ml: "auto !important" }}><OpenInNewRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip> : null}
              </Stack>
            </Box>
          );
        })}
      </Box>

      <OwnerInitiativeDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={(created) => {
        setCreateOpen(false);
        if (created) openProject(created);
        else load();
      }} />
    </Box>
  );
}
